import { useState, useEffect, useMemo, useRef, Fragment } from 'react'
import {
    Box, Grid, Paper, Typography, Button, IconButton,
    Switch, FormControlLabel, TextField, Select, MenuItem,
    FormControl, InputLabel, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
    DialogContentText, ToggleButton, ToggleButtonGroup, Collapse
} from '@mui/material'
import {
    ArrowBackIos, Refresh, Sync, AccessTime, PlayArrow, Settings, Alarm,
    ChevronLeft, ChevronRight, History, FlashOn, KeyboardArrowDown, KeyboardArrowUp, Warning, Stop
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useStatus } from '../../context/Status.context'
import { cronsRoutes, syncRoutes } from '../../routes'
import { GenerationStatusModal } from '../../dialogs/StatusModal'

// Formateador de tiempo restante (Horas a Días: si supera 23h 59m cambia a DD HH)
const formatTimeRemaining = (diffMs) => {
    if (diffMs === Infinity || !diffMs || diffMs < 0) return 'Inactivo'

    const totalMinutes = Math.floor(diffMs / (60 * 1000))
    const totalHours = Math.floor(totalMinutes / 60)
    const days = Math.floor(totalHours / 24)
    const hours = totalHours % 24
    const minutes = totalMinutes % 60

    if (days > 0) {
        return `En ${days}d ${hours}h`
    } else if (hours > 0) {
        return `En ${hours}h ${minutes}m`
    } else {
        return `En ${minutes} min`
    }
}

const calculateNextSync = (cron) => {
    if (!cron || !cron.enabled || !cron.time) {
        return { nextDate: null, diffMs: Infinity, label: 'Deshabilitado', remainingText: 'Inactivo', formattedTime: '--:--', formattedDate: '--/--' }
    }

    const [startH, startM] = cron.time.split(':').map(Number)
    const now = new Date()
    const repeatVal = parseInt(cron.repeatValue) || 1
    const repeatUnit = cron.repeatUnit || 'hours'

    let nextDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), startH || 0, startM || 0, 0, 0)

    if (repeatUnit === 'days') {
        while (nextDate <= now) {
            nextDate.setDate(nextDate.getDate() + repeatVal)
        }
    } else {
        // 'hours'
        if (nextDate > now) {
            while (nextDate.getTime() - (repeatVal * 3600 * 1000) >= now.getTime()) {
                nextDate = new Date(nextDate.getTime() - (repeatVal * 3600 * 1000))
            }
        } else {
            const diffHours = (now.getTime() - nextDate.getTime()) / (3600 * 1000)
            const steps = Math.ceil(diffHours / repeatVal)
            nextDate = new Date(nextDate.getTime() + (steps * repeatVal * 3600 * 1000))
            if (nextDate <= now) {
                nextDate = new Date(nextDate.getTime() + (repeatVal * 3600 * 1000))
            }
        }
    }

    const diffMs = nextDate.getTime() - now.getTime()
    const remainingText = formatTimeRemaining(diffMs)

    const formattedTime = nextDate.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
    const formattedDate = nextDate.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' })
    const label = `${formattedDate} ${formattedTime}`

    return { nextDate, diffMs, label, remainingText, formattedTime, formattedDate }
}

const AdminCronsSyncPage = () => {
    const navigate = useNavigate()
    const { setstatus } = useStatus()
    const topSliderRef = useRef(null)
    const cronSettingsSliderRef = useRef(null)

    const [allCrons, setAllCrons] = useState([])
    const [editingCrons, setEditingCrons] = useState({})
    const [syncStatus, setSyncStatus] = useState(null)
    const [syncHistory, setSyncHistory] = useState([])
    const [historyPage, setHistoryPage] = useState(1)
    const [hasMoreHistory, setHasMoreHistory] = useState(false)
    const [loadingHistory, setLoadingHistory] = useState(false)
    const [expandedHistoryRows, setExpandedHistoryRows] = useState({})
    const [loading, setLoading] = useState(false)

    const toggleHistoryRowExpand = (id) => {
        setExpandedHistoryRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }))
    }

    // Right Column Switch View: 'actions' or 'history'
    const [rightTab, setRightTab] = useState('actions')

    // Sync Confirmation Dialog State
    const [confirmSyncModalOpen, setConfirmSyncModalOpen] = useState(false)
    const [confirmResetLockModalOpen, setConfirmResetLockModalOpen] = useState(false)
    const [isAborting, setIsAborting] = useState(false)
    const [selectedSyncType, setSelectedSyncType] = useState(null)
    const [selectedSyncLabel, setSelectedSyncLabel] = useState('')

    const fetchCrons = async () => {
        try {
            const res = await cronsRoutes.getAll()
            if (res && res.data) {
                setAllCrons(res.data)
                const initialEditing = {}
                res.data.forEach(c => {
                    initialEditing[c.name] = { ...c }
                })
                setEditingCrons(initialEditing)
            }
        } catch (err) {
            console.error('Error fetching crons:', err)
        }
    }

    const fetchSyncStatus = async () => {
        try {
            const res = await syncRoutes.getStatus()
            if (res && res.data) {
                setSyncStatus(res.data)
            }
        } catch (err) {
            console.error('Error fetching sync status:', err)
        }
    }

    const fetchSyncHistory = async (reset = false) => {
        const targetPage = reset ? 1 : historyPage
        setLoadingHistory(true)
        try {
            const res = await syncRoutes.getSyncHistory(targetPage, 10)
            if (res && res.data) {
                const logsData = Array.isArray(res.data.logs) ? res.data.logs : (Array.isArray(res.data) ? res.data : [])
                const hasMore = res.data.hasMore ?? false

                setSyncHistory(prev => (reset ? logsData : [...prev, ...logsData]))
                setHasMoreHistory(hasMore)
                setHistoryPage(reset ? 2 : targetPage + 1)
            }
        } catch (err) {
            console.error('Error fetching sync history:', err)
        } finally {
            setLoadingHistory(false)
        }
    }

    const loadData = async () => {
        setLoading(true)
        await Promise.all([fetchCrons(), fetchSyncStatus(), fetchSyncHistory(true)])
        setLoading(false)
    }

    useEffect(() => {
        loadData()

        let eventSource = null
        try {
            const streamUrl = syncRoutes.getStreamUrl()
            eventSource = new EventSource(streamUrl)
            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    if (data) {
                        setSyncStatus(data)
                        if (data.isSyncing === false) {
                            fetchSyncHistory(true)
                        }
                    }
                } catch (e) {
                    console.error('Error parsing SSE event:', e)
                }
            }
            eventSource.onerror = (err) => {
                if (eventSource) eventSource.close()
            }
        } catch (err) {
            console.error('SSE initialization error:', err)
        }

        return () => {
            if (eventSource) {
                eventSource.close()
            }
        }
    }, [])

    const handleCronEditChange = (name, field, value) => {
        setEditingCrons(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                [field]: value
            }
        }))
    }

    const isCronModified = (cronName) => {
        const original = allCrons.find(c => c.name === cronName)
        const edited = editingCrons[cronName]
        if (!original || !edited) return false
        return (
            String(edited.time) !== String(original.time) ||
            parseInt(edited.repeatValue, 10) !== parseInt(original.repeatValue, 10) ||
            String(edited.repeatUnit) !== String(original.repeatUnit) ||
            Boolean(edited.enabled) !== Boolean(original.enabled)
        )
    }

    const handleResetCron = (cronName) => {
        const original = allCrons.find(c => c.name === cronName)
        if (original) {
            setEditingCrons(prev => ({
                ...prev,
                [cronName]: { ...original }
            }))
        }
    }

    const handleSaveCron = async (name) => {
        const data = editingCrons[name]
        if (!data) return
        setLoading(true)
        try {
            await cronsRoutes.update(name, {
                time: data.time,
                repeatValue: parseInt(data.repeatValue),
                repeatUnit: data.repeatUnit,
                enabled: data.enabled
            })
            setstatus({
                status: 'completed',
                title: 'Cron Actualizado',
                message: `Configuración de '${name}' guardada correctamente.`,
                severity: 'success'
            })
            await fetchCrons()
        } catch (error) {
            console.error('Error updating cron:', error)
            setstatus({
                status: 'error',
                title: 'Error al actualizar Cron',
                message: error.response?.data?.message || error.message,
                severity: 'error'
            })
        } finally {
            setLoading(false)
        }
    }

    // Toggle All Crons Master Switch
    const handleToggleAllCrons = async (targetState) => {
        setLoading(true)
        try {
            await Promise.all(
                allCrons.map(c => {
                    const data = editingCrons[c.name] || c
                    return cronsRoutes.update(c.name, {
                        time: data.time,
                        repeatValue: parseInt(data.repeatValue),
                        repeatUnit: data.repeatUnit,
                        enabled: targetState
                    })
                })
            )
            setstatus({
                status: 'completed',
                title: 'Crons Actualizados',
                message: `Todas las tareas fueron ${targetState ? 'activadas' : 'desactivadas'} exitosamente.`,
                severity: 'success'
            })
            await fetchCrons()
        } catch (err) {
            console.error('Error toggling all crons:', err)
            setstatus({
                status: 'error',
                title: 'Error al actualizar crons',
                message: 'No se pudieron actualizar todos los crons.',
                severity: 'error'
            })
        } finally {
            setLoading(false)
        }
    }

    // Sorted Crons by Next Sync Time (Closest next sync always at far left)
    const sortedCronsWithCalcs = useMemo(() => {
        const list = allCrons.map(cron => {
            const currentData = editingCrons[cron.name] || cron
            const calc = calculateNextSync(currentData)
            return { cron, currentData, calc }
        })

        list.sort((a, b) => {
            if (a.currentData.enabled && !b.currentData.enabled) return -1
            if (!a.currentData.enabled && b.currentData.enabled) return 1
            return a.calc.diffMs - b.calc.diffMs
        })

        return list
    }, [allCrons, editingCrons])

    const allAreEnabled = useMemo(() => {
        if (allCrons.length === 0) return false
        return allCrons.every(c => (editingCrons[c.name]?.enabled ?? c.enabled))
    }, [allCrons, editingCrons])

    // Scroll Slider Controls
    const handleScrollTopSlider = (direction) => {
        if (topSliderRef.current) {
            const scrollAmount = direction === 'left' ? -260 : 260
            topSliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
        }
    }

    const handleScrollSettingsSlider = (direction) => {
        if (cronSettingsSliderRef.current) {
            const scrollAmount = direction === 'left' ? -300 : 300
            cronSettingsSliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
        }
    }

    const handleOpenConfirmSync = (type, label) => {
        setSelectedSyncType(type)
        setSelectedSyncLabel(label)
        setConfirmSyncModalOpen(true)
    }

    const handleExecuteSync = async () => {
        if (!selectedSyncType) return
        setConfirmSyncModalOpen(false)

        setstatus({
            status: 'loading',
            title: `Sincronizando ${selectedSyncLabel}`,
            message: 'Ejecutando proceso de sincronización con el servidor... Por favor espere.'
        })

        try {
            if (selectedSyncType === 'all') {
                await syncRoutes.syncAll()
            } else if (selectedSyncType === 'reports') {
                await syncRoutes.syncReports()
            } else if (selectedSyncType === 'patterns') {
                await syncRoutes.syncPatterns()
            } else if (selectedSyncType === 'machines') {
                await syncRoutes.syncMachines()
            } else if (selectedSyncType === 'sites') {
                await syncRoutes.syncSites()
            }

            setstatus({
                status: 'completed',
                title: 'Sincronización Finalizada',
                message: `Proceso de sincronización de ${selectedSyncLabel} completado exitosamente.`,
                severity: 'success'
            })
        } catch (err) {
            console.error('Error executing sync:', err)
            const errorMsg = err?.response?.data?.message || err?.message || `Ocurrió un error al sincronizar ${selectedSyncLabel}.`
            setstatus({
                status: 'error',
                title: 'Error en Sincronización',
                message: errorMsg,
                severity: 'error'
            })
        } finally {
            setSyncStatus({ isSyncing: false, type: null, action: null, progress: null })
            setRightTab('history')
            await Promise.all([fetchSyncStatus(), fetchSyncHistory(true)])
        }
    }

    const handleResetLock = async () => {
        try {
            await syncRoutes.resetLock()
            setSyncStatus({ isSyncing: false, type: null, action: null, progress: null })
            setRightTab('history')
            setstatus({
                status: 'completed',
                title: 'Bloqueo Liberado',
                message: 'El bloqueo de sincronización fue liberado exitosamente.',
                severity: 'success'
            })
            await Promise.all([fetchSyncStatus(), fetchSyncHistory(true)])
        } catch (err) {
            console.error('Error resetting sync lock:', err)
            setstatus({
                status: 'error',
                title: 'Error al liberar bloqueo',
                message: 'No se pudo liberar el bloqueo de sincronización.',
                severity: 'error'
            })
        }
    }

    const handleAbortSync = async () => {
        setIsAborting(true)
        try {
            await syncRoutes.abortSync()
            setSyncStatus({ isSyncing: false, type: null, action: null, progress: null })
            setRightTab('history')
            setstatus({
                status: 'completed',
                title: 'Sincronización Detenida',
                message: 'La sincronización se detuvo. Se actualizó el historial con los registros procesados hasta el momento.',
                severity: 'info'
            })
            await Promise.all([fetchSyncStatus(), fetchSyncHistory(true)])
        } catch (err) {
            console.error('Error aborting sync:', err)
            setstatus({
                status: 'error',
                title: 'Error al cancelar',
                message: 'No se pudo enviar la orden de cancelación.',
                severity: 'error'
            })
        } finally {
            setIsAborting(false)
        }
    }

    return (
        <Box sx={{ pt: 10, px: 2, pb: 1, backgroundColor: '#f4f6f8', minHeight: '100vh', boxSizing: 'border-box' }}>
            {/* TOOLBAR SUPERIOR COMPACTO */}
            <Paper elevation={0} sx={{ p: 1, mb: 1, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate(-1)} size="small">
                        <ArrowBackIos sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Administración - Sincronización y Crons
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={allAreEnabled}
                                onChange={(e) => handleToggleAllCrons(e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={<Typography variant="caption" sx={{ fontWeight: 'bold' }}>Activar Todos</Typography>}
                    />
                    {syncStatus?.isSyncing && (
                        <Button
                            startIcon={isAborting ? <CircularProgress size={14} color="inherit" /> : <Stop />}
                            onClick={handleAbortSync}
                            disabled={isAborting}
                            color="error"
                            variant="contained"
                            size="small"
                            sx={{ height: 30, fontSize: '0.75rem', fontWeight: 'bold' }}
                        >
                            {isAborting ? 'Deteniendo...' : 'Detener Sincronización'}
                        </Button>
                    )}
                    <Button startIcon={<FlashOn />} onClick={() => setConfirmResetLockModalOpen(true)} color="warning" variant="outlined" size="small" sx={{ height: 30, fontSize: '0.75rem' }}>
                        Liberar Bloqueo
                    </Button>
                    <Button startIcon={<Refresh />} onClick={loadData} disabled={loading} variant="outlined" size="small" sx={{ height: 30, fontSize: '0.75rem' }}>
                        Refrescar
                    </Button>
                </Box>
            </Paper>
            <Grid container spacing={1}>
                <Grid item xs={12} md={9}>
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2, backgroundColor: '#fff' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTime color="primary" fontSize="small" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    Próximas Ejecuciones Programadas
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton size="small" onClick={() => handleScrollTopSlider('left')} sx={{ border: '1px solid #e0e0e0', p: 0.5 }}>
                                    <ChevronLeft fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleScrollTopSlider('right')} sx={{ border: '1px solid #e0e0e0', p: 0.5 }}>
                                    <ChevronRight fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>

                        <Box
                            ref={topSliderRef}
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                overflowX: 'auto',
                                pb: 1,
                                pt: 0.5,
                                scrollSnapType: 'x mandatory',
                                WebkitOverflowScrolling: 'touch',
                                '&::-webkit-scrollbar': { height: 5 },
                                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 3 },
                                '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: 3 }
                            }}
                        >
                            {sortedCronsWithCalcs.map(({ cron, currentData, calc }, index) => {
                                const isSyncing = syncStatus?.isSyncing || syncStatus?.inProgress
                                const activeSyncType = isSyncing ? (syncStatus?.type || 'all') : null
                                const cronCleanName = cron.name.toLowerCase().replace('sync_', '')
                                const isActiveSync = activeSyncType && (activeSyncType === 'all' || activeSyncType === cron.name || activeSyncType.toLowerCase().includes(cronCleanName))

                                // The closest upcoming sync is the first enabled card that is NOT currently active
                                const isClosest = !isActiveSync && currentData.enabled && sortedCronsWithCalcs.findIndex(item => item.currentData.enabled && !(activeSyncType && (activeSyncType === 'all' || activeSyncType === item.cron.name || activeSyncType.toLowerCase().includes(item.cron.name.toLowerCase().replace('sync_', ''))))) === index

                                let bgColor = '#fafafa'
                                let borderColor = '#e0e0e0'
                                let borderWidth = 1
                                let boxShadow = 'none'

                                if (isActiveSync) {
                                    bgColor = '#fffde7'
                                    borderColor = '#fbc02d'
                                    borderWidth = 2
                                    boxShadow = '0 2px 10px rgba(251, 192, 45, 0.35)'
                                } else if (isClosest) {
                                    bgColor = '#e3f2fd'
                                    borderColor = '#1976d2'
                                    borderWidth = 2
                                    boxShadow = '0 2px 8px rgba(25, 118, 210, 0.15)'
                                } else if (!currentData.enabled) {
                                    bgColor = '#f8f8f8'
                                }

                                return (
                                    <Paper
                                        key={cron.name}
                                        variant="outlined"
                                        sx={{
                                            minWidth: (isActiveSync || isClosest) ? 230 : 190,
                                            maxWidth: (isActiveSync || isClosest) ? 250 : 210,
                                            flexShrink: 0,
                                            scrollSnapAlign: 'start',
                                            p: 1.5,
                                            borderRadius: 2,
                                            transition: 'all 0.2s ease',
                                            backgroundColor: bgColor,
                                            borderColor: borderColor,
                                            borderWidth: borderWidth,
                                            boxShadow: boxShadow
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: isActiveSync ? '#f57f17' : (isClosest ? '#1565c0' : 'text.primary'), textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                                {cron.name}
                                            </Typography>
                                            {isActiveSync ? (
                                                <Chip label="EN PROCESO" color="warning" size="small" sx={{ fontWeight: 'bold', height: 18, fontSize: '0.6rem' }} />
                                            ) : isClosest ? (
                                                <Chip label="MÁS CERCANA" color="primary" size="small" sx={{ fontWeight: 'bold', height: 18, fontSize: '0.6rem' }} />
                                            ) : null}
                                        </Box>

                                        {isActiveSync ? (
                                            <>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f57f17', mt: 0.5 }}>
                                                    Sincronizando...
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                                    {syncStatus.action || 'Ejecutando proceso'}
                                                </Typography>
                                            </>
                                        ) : currentData.enabled ? (
                                            <>
                                                <Typography
                                                    variant={isClosest ? "subtitle1" : "body2"}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: isClosest ? '#0d47a1' : 'text.primary',
                                                        mt: 0.5
                                                    }}
                                                >
                                                    {calc.remainingText}
                                                </Typography>

                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                                    Inicio: <strong>{currentData.time}</strong> | Cada {currentData.repeatValue} {currentData.repeatUnit === 'hours' ? 'hrs' : 'días'}
                                                </Typography>
                                                <Typography variant="caption" color={isClosest ? "primary" : "textSecondary"} sx={{ display: 'block', fontSize: '0.68rem', fontWeight: isClosest ? 'bold' : 'normal' }}>
                                                    Próxima: {calc.label}
                                                </Typography>
                                            </>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                                Deshabilitada
                                            </Typography>
                                        )}
                                    </Paper>
                                )
                            })}
                        </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#fff' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Alarm color="primary" fontSize="small" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    Ajustes de Crons
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton size="small" onClick={() => handleScrollSettingsSlider('left')} sx={{ border: '1px solid #e0e0e0', p: 0.5 }}>
                                    <ChevronLeft fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleScrollSettingsSlider('right')} sx={{ border: '1px solid #e0e0e0', p: 0.5 }}>
                                    <ChevronRight fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>

                        <Box
                            ref={cronSettingsSliderRef}
                            sx={{
                                display: 'flex',
                                gap: 2,
                                overflowX: 'auto',
                                pb: 1,
                                pt: 0.5,
                                scrollSnapType: 'x mandatory',
                                WebkitOverflowScrolling: 'touch',
                                '&::-webkit-scrollbar': { height: 5 },
                                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 3 },
                                '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: 3 }
                            }}
                        >
                            {allCrons.map((cron) => {
                                const editData = editingCrons[cron.name] || cron
                                const calc = calculateNextSync(editData)

                                return (
                                    <Paper
                                        key={cron.name}
                                        variant="outlined"
                                        sx={{
                                            minWidth: 260,
                                            maxWidth: 290,
                                            flexShrink: 0,
                                            scrollSnapAlign: 'start',
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: '#fdfdfd',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justify: 'space-between'
                                        }}
                                    >
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ fontSize: '0.85rem' }}>
                                                    {cron.name}
                                                </Typography>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={!!editData.enabled}
                                                            onChange={(e) => handleCronEditChange(cron.name, 'enabled', e.target.checked)}
                                                            size="small"
                                                        />
                                                    }
                                                    label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Activo</Typography>}
                                                />
                                            </Box>

                                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1, minHeight: 28, fontSize: '0.7rem', lineHeight: 1.2 }}>
                                                {cron.description || `Tarea para ${cron.name}`}
                                            </Typography>

                                            <Grid container spacing={1}>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        label="Inicio"
                                                        type="time"
                                                        size="small"
                                                        fullWidth
                                                        value={editData.time || '00:00'}
                                                        onChange={(e) => handleCronEditChange(cron.name, 'time', e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                        inputProps={{ style: { padding: '4px 6px', fontSize: '0.75rem' } }}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        label="Cada"
                                                        type="number"
                                                        size="small"
                                                        fullWidth
                                                        value={editData.repeatValue || 1}
                                                        onChange={(e) => handleCronEditChange(cron.name, 'repeatValue', e.target.value)}
                                                        inputProps={{ style: { padding: '4px 6px', fontSize: '0.75rem' } }}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Unidad</InputLabel>
                                                        <Select
                                                            value={editData.repeatUnit || 'hours'}
                                                            label="Unidad"
                                                            onChange={(e) => handleCronEditChange(cron.name, 'repeatUnit', e.target.value)}
                                                            sx={{ fontSize: '0.75rem', height: 32 }}
                                                        >
                                                            <MenuItem value="hours" sx={{ fontSize: '0.75rem' }}>Horas</MenuItem>
                                                            <MenuItem value="days" sx={{ fontSize: '0.75rem' }}>Días</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>

                                            <Box sx={{ mt: 1, p: 0.8, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
                                                    Próxima: <strong>{calc.label}</strong>
                                                </Typography>
                                                <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', fontSize: '0.7rem' }}>
                                                    Restante: {calc.remainingText}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                                            {isCronModified(cron.name) && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="inherit"
                                                    onClick={() => handleResetCron(cron.name)}
                                                    disabled={loading}
                                                    sx={{ py: 0.5, fontSize: '0.72rem' }}
                                                >
                                                    Cancelar
                                                </Button>
                                            )}
                                            <Button
                                                size="small"
                                                variant="contained"
                                                fullWidth
                                                onClick={() => handleSaveCron(cron.name)}
                                                disabled={loading || !isCronModified(cron.name)}
                                                startIcon={<Settings sx={{ fontSize: 14 }} />}
                                                sx={{ py: 0.5, fontSize: '0.75rem' }}
                                            >
                                                Guardar
                                            </Button>
                                        </Box>
                                    </Paper>
                                )
                            })}
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, backgroundColor: '#fff', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {rightTab === 'actions' ? 'Acciones' : 'Historial'}
                            </Typography>
                            <ToggleButtonGroup
                                value={rightTab}
                                exclusive
                                onChange={(e, newTab) => { if (newTab !== null) setRightTab(newTab) }}
                                size="small"
                            >
                                <ToggleButton value="actions" sx={{ py: 0.3, px: 1, fontSize: '0.72rem' }}>
                                    Acciones
                                </ToggleButton>
                                <ToggleButton value="history" sx={{ py: 0.3, px: 1, fontSize: '0.72rem' }}>
                                    Historial
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {rightTab === 'actions' ? (
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<PlayArrow />}
                                        onClick={() => handleOpenConfirmSync('reports', 'Reportes')}
                                        sx={{ fontSize: '0.75rem' }}
                                    >
                                        Sync Reportes
                                    </Button>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<PlayArrow />}
                                        onClick={() => handleOpenConfirmSync('patterns', 'Pautas')}
                                        sx={{ fontSize: '0.75rem' }}
                                    >
                                        Sync Pautas
                                    </Button>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        color="info"
                                        startIcon={<PlayArrow />}
                                        onClick={() => handleOpenConfirmSync('machines', 'Máquinas')}
                                        sx={{ fontSize: '0.75rem' }}
                                    >
                                        Sync Máquinas
                                    </Button>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        startIcon={<PlayArrow />}
                                        onClick={() => handleOpenConfirmSync('sites', 'Sitios')}
                                        sx={{ fontSize: '0.75rem' }}
                                    >
                                        Sync Sitios
                                    </Button>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        size="small"
                                        variant="contained"
                                        color="warning"
                                        startIcon={<Sync />}
                                        onClick={() => handleOpenConfirmSync('all', 'TODO')}
                                        sx={{ fontSize: '0.75rem' }}
                                    >
                                        Sincronizar TODO
                                    </Button>
                                </Grid>
                            </Grid>
                        ) : (
                            <Box sx={{ flexGrow: 1 }}>
                                {syncHistory.length > 0 ? (
                                    <>
                                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: '0.7rem', p: 0.8 }}><strong>Tipo</strong></TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', p: 0.8 }}><strong>Resumen</strong></TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', p: 0.8 }}><strong>Fecha</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {syncHistory.map((item, idx) => {
                                                    const rowId = item._id || idx
                                                    const isExpanded = !!expandedHistoryRows[rowId]

                                                    // Precise category detection
                                                    const categories = ['reports', 'machines', 'patterns', 'units', 'sites']
                                                    const foundCats = categories.filter(c => !!item[c])
                                                    const detectedType = foundCats.length === 1 ? foundCats[0] : (item.type && item.type !== 'all' ? item.type : 'all')

                                                    const typeMap = {
                                                        all: 'General',
                                                        reports: 'Reportes',
                                                        machines: 'Máquinas',
                                                        patterns: 'Pautas',
                                                        units: 'Unidades',
                                                        sites: 'Sitios'
                                                    }
                                                    const typeLabel = typeMap[detectedType] || detectedType || item.action || 'General'

                                                    // Calculate summary metrics
                                                    let created = 0, updated = 0, failed = 0
                                                    if (detectedType === 'all') {
                                                        categories.forEach(cat => {
                                                            if (item[cat]) {
                                                                created += item[cat].created || 0
                                                                updated += item[cat].updated || 0
                                                            }
                                                        })
                                                    } else if (item[detectedType]) {
                                                        created = item[detectedType].created || 0
                                                        updated = item[detectedType].updated || 0
                                                        failed = item[detectedType].failed || 0
                                                    }

                                                    return (
                                                        <Fragment key={rowId}>
                                                            <TableRow
                                                                hover
                                                                onClick={() => toggleHistoryRowExpand(rowId)}
                                                                sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
                                                            >
                                                                <TableCell sx={{ fontSize: '0.68rem', p: 0.6, fontWeight: 'bold' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                                        <IconButton size="small" sx={{ p: 0.1 }}>
                                                                            {isExpanded ? <KeyboardArrowUp sx={{ fontSize: 13 }} /> : <KeyboardArrowDown sx={{ fontSize: 13 }} />}
                                                                        </IconButton>
                                                                        {typeLabel}
                                                                        {(item.status === 'stopped' || item.status === 'aborted' || item.status === 'cancelled') && (
                                                                            <Chip label="Detenido" size="small" color="warning" sx={{ height: 14, fontSize: '0.55rem', px: 0.2, ml: 0.3 }} />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: '0.68rem', p: 0.6 }}>
                                                                    <Typography variant="caption" color="success.main" sx={{ mr: 0.5, fontWeight: 'bold' }}>+{created}</Typography>
                                                                    <Typography variant="caption" color="primary.main" sx={{ mr: 0.5, fontWeight: 'bold' }}>~{updated}</Typography>
                                                                    {failed > 0 && <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold' }}>!{failed}</Typography>}
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: '0.65rem', p: 0.6 }}>
                                                                    {item.createdAt || item.startTime ? new Date(item.createdAt || item.startTime).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '--'}
                                                                </TableCell>
                                                            </TableRow>

                                                            <TableRow>
                                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                                                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                        <Box sx={{ py: 0.5 }}>
                                                                            {categories.map(cat => {
                                                                                const summary = item[cat]
                                                                                if (!summary) return null

                                                                                const catLabel = typeMap[cat] || cat
                                                                                const details = summary.details || {}
                                                                                const createdList = details.created || []
                                                                                const updatedList = details.updated || []
                                                                                const failedList = details.failedIds || details.failed || []

                                                                                return (
                                                                                    <Box key={cat} sx={{ mb: 0.5, p: 0.5, backgroundColor: '#f9fafb', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                                                                        {Array.isArray(createdList) && createdList.length > 0 && (
                                                                                            <Box sx={{ mt: 0.2 }}>
                                                                                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold', fontSize: '0.62rem', display: 'block', mb: 0.1 }}>
                                                                                                    Creados ({createdList.length}):
                                                                                                </Typography>
                                                                                                <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', maxHeight: 80, overflowY: 'auto' }}>
                                                                                                    {createdList.map((id, i) => (
                                                                                                        <Chip key={i} label={id} size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: '0.58rem', px: 0.2 }} />
                                                                                                    ))}
                                                                                                </Box>
                                                                                            </Box>
                                                                                        )}

                                                                                        {Array.isArray(updatedList) && updatedList.length > 0 && (
                                                                                            <Box sx={{ mt: 0.2 }}>
                                                                                                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'bold', fontSize: '0.62rem', display: 'block', mb: 0.1 }}>
                                                                                                    Actualizados ({updatedList.length}):
                                                                                                </Typography>
                                                                                                <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', maxHeight: 80, overflowY: 'auto' }}>
                                                                                                    {updatedList.map((id, i) => (
                                                                                                        <Chip key={i} label={id} size="small" color="primary" variant="outlined" sx={{ height: 16, fontSize: '0.58rem', px: 0.2 }} />
                                                                                                    ))}
                                                                                                </Box>
                                                                                            </Box>
                                                                                        )}

                                                                                        {Array.isArray(failedList) && failedList.length > 0 && (
                                                                                            <Box sx={{ mt: 0.2 }}>
                                                                                                <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold', fontSize: '0.62rem', display: 'block', mb: 0.1 }}>
                                                                                                    Fallados ({failedList.length}):
                                                                                                </Typography>
                                                                                                <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', maxHeight: 80, overflowY: 'auto' }}>
                                                                                                    {failedList.map((id, i) => (
                                                                                                        <Chip key={i} label={id} size="small" color="error" variant="outlined" sx={{ height: 16, fontSize: '0.58rem', px: 0.2 }} />
                                                                                                    ))}
                                                                                                </Box>
                                                                                            </Box>
                                                                                        )}
                                                                                    </Box>
                                                                                )
                                                                            })}
                                                                        </Box>
                                                                    </Collapse>
                                                                </TableCell>
                                                            </TableRow>
                                                        </Fragment>
                                                    )
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {hasMoreHistory && (
                                        <Box sx={{ textAlign: 'center', mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => fetchSyncHistory(false)}
                                                disabled={loadingHistory}
                                                sx={{ fontSize: '0.72rem', py: 0.2 }}
                                            >
                                                {loadingHistory ? <CircularProgress size={14} /> : 'Ver más (+10)'}
                                            </Button>
                                        </Box>
                                    )}
                                    </>
                                ) : syncStatus ? (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: '0.7rem', p: 0.8 }}><strong>Entidad</strong></TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', p: 0.8 }}><strong>Fecha</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: '0.68rem', p: 0.8 }}>Reportes</TableCell>
                                                    <TableCell sx={{ fontSize: '0.68rem', p: 0.8 }}>
                                                        {syncStatus.lastReportsSync ? new Date(syncStatus.lastReportsSync).toLocaleString('es-CL') : 'Sin registro'}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: '0.68rem', p: 0.8 }}>Pautas</TableCell>
                                                    <TableCell sx={{ fontSize: '0.68rem', p: 0.8 }}>
                                                        {syncStatus.lastPatternsSync ? new Date(syncStatus.lastPatternsSync).toLocaleString('es-CL') : 'Sin registro'}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: '0.68rem', p: 0.8 }}>Máquinas</TableCell>
                                                    <TableCell sx={{ fontSize: '0.68rem', p: 0.8 }}>
                                                        {syncStatus.lastMachinesSync ? new Date(syncStatus.lastMachinesSync).toLocaleString('es-CL') : 'Sin registro'}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography variant="caption" color="textSecondary">
                                        No hay historial registrado.
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Dialog open={confirmSyncModalOpen} onClose={() => setConfirmSyncModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    Confirmar Sincronización Manual
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#333' }}>
                        ¿Está seguro que desea ejecutar manualmente la sincronización de <strong>{selectedSyncLabel}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setConfirmSyncModalOpen(false)} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleExecuteSync} variant="contained" color="warning">
                        Ejecutar Sync
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmResetLockModalOpen} onClose={() => setConfirmResetLockModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FlashOn color="warning" />
                    Desbloquear Sincronización
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#333' }}>
                        ¿Deseas liberar el bloqueo de sincronización? Esto permitirá iniciar nuevas sincronizaciones de inmediato.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setConfirmResetLockModalOpen(false)} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => {
                            setConfirmResetLockModalOpen(false)
                            handleResetLock()
                        }}
                        variant="contained"
                        color="warning"
                    >
                        Desbloquear
                    </Button>
                </DialogActions>
            </Dialog>

            <GenerationStatusModal />
        </Box>
    )
}

export default AdminCronsSyncPage
